import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ShieldCheck,
  FirstAidKit,
  Pill,
  Syringe,
  Heartbeat,
  HouseLine,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { FAQ } from '@/components/FAQ';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: 'easeOut' },
};

const PlanoSaude = () => {
  const situacoes = [
    { icon: Syringe, title: 'Negativa de cirurgia' },
    { icon: FirstAidKit, title: 'Negativa de exame' },
    { icon: Pill, title: 'Negativa de medicamento' },
    { icon: Heartbeat, title: 'Negativa de tratamento' },
    { icon: HouseLine, title: 'Negativa de home care' },
    { icon: ShieldCheck, title: 'Demora injustificada na autorização' },
  ];

  const faqItems = [
    {
      question: 'O que fazer quando o plano de saúde nega um procedimento?',
      answer:
        'É importante reunir a negativa, os documentos médicos e as informações do caso para análise jurídica adequada.',
    },
    {
      question: 'Casos urgentes podem ser analisados?',
      answer:
        'Situações urgentes exigem avaliação cuidadosa e célere da documentação e do contexto clínico, para definição da estratégia juridicamente cabível.',
    },
    {
      question: 'Apenas cirurgias negadas podem ser analisadas?',
      answer:
        'Não. Exames, medicamentos, tratamentos, internações, home care e outras recusas relacionadas à cobertura também podem exigir análise jurídica.',
    },
    {
      question: 'A RDC Advogados atende casos no Rio de Janeiro?',
      answer:
        'Sim. O escritório está localizado no Centro do Rio de Janeiro e atua na análise de demandas relacionadas a negativas de plano de saúde com orientação técnica e responsável.',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-24 bg-[#2A2A2A] text-white" data-testid="plano-saude-hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="max-w-4xl">
            <h1 className="text-4xl sm:text-5xl font-bold playfair tracking-tight mb-6">
              Ação contra Plano de Saúde no Rio de Janeiro
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              Análise jurídica de negativas de cobertura com atuação responsável, leitura técnica do caso e atenção
              à urgência que a situação pode exigir.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Opening Text */}
      <section className="py-24 bg-white" data-testid="plano-saude-intro-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="max-w-4xl mx-auto">
            <p className="text-lg text-[#737373] leading-relaxed mb-6">
              Casos envolvendo negativa de plano de saúde costumam atingir o cliente em momentos de especial
              vulnerabilidade. Cirurgia recusada, medicamento negado, exame não autorizado, internação indeferida ou
              demora indevida na cobertura podem gerar insegurança imediata e exigir orientação jurídica célere e
              tecnicamente segura.
            </p>
            <p className="text-lg text-[#737373] leading-relaxed">
              A RDC Advogados atua na análise dessas situações com sobriedade, atenção documental e definição
              estratégica das medidas cabíveis, sempre com foco na seriedade que demandas relacionadas à saúde
              exigem.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Situações */}
      <section className="py-24 bg-[#FAFAFA]" data-testid="plano-saude-situacoes-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#2A2A2A] playfair mb-4">
              Situações frequentemente analisadas
            </h2>
            <p className="text-lg text-[#737373]">Negativas que podem exigir avaliação jurídica</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {situacoes.map((item, index) => (
              <motion.div
                key={item.title || item.id || index}
                {...fadeUp}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                className="bg-white border border-[#2A2A2A]/10 rounded-sm p-6 flex items-center space-x-4"
                data-testid={`situacao-${index}`}
              >
                <item.icon size={32} weight="duotone" className="text-[#A98C67] flex-shrink-0" />
                <span className="text-[#2A2A2A] font-medium">{item.title}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How we analyze */}
      <section className="py-24 bg-white" data-testid="plano-saude-analysis-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeUp}>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#2A2A2A] playfair mb-6">
                Como o escritório conduz a análise
              </h2>
              <p className="text-xl text-[#737373] mb-6 leading-relaxed">Atuação técnica em casos sensíveis</p>
              <p className="text-lg text-[#737373] leading-relaxed">
                O atendimento parte da análise da negativa apresentada, da documentação médica disponível e da
                urgência concreta da situação. Com base nesses elementos, o escritório orienta o cliente sobre o
                cenário jurídico, as providências possíveis e a medida adequada ao caso.
              </p>
            </motion.div>
            <motion.div {...fadeUp} transition={{ delay: 0.2, duration: 0.6 }}>
              <img
                src="https://images.pexels.com/photos/175045/pexels-photo-175045.jpeg"
                alt="Documentação médica"
                className="rounded-sm shadow-lg w-full h-auto"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Sobriety */}
      <section className="py-24 bg-[#FAFAFA]" data-testid="plano-saude-sobriety-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#2A2A2A] playfair mb-6">
              Atendimento com sobriedade e precisão
            </h2>
            <p className="text-xl text-[#737373] mb-8 leading-relaxed">
              Quando o direito à saúde exige resposta jurídica consistente
            </p>
            <p className="text-lg text-[#737373] leading-relaxed">
              Demandas contra plano de saúde não comportam improviso. Elas exigem leitura técnica, firmeza
              institucional e sensibilidade para compreender a urgência envolvida. Na RDC Advogados, a condução
              dessas situações é feita com organização, clareza e responsabilidade.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-white" data-testid="plano-saude-faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#2A2A2A] playfair mb-4">Perguntas frequentes</h2>
          </motion.div>
          <motion.div {...fadeUp}>
            <FAQ items={faqItems} />
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#2A2A2A] text-white" data-testid="plano-saude-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl sm:text-4xl font-bold playfair mb-6">
              Negativa de plano de saúde exige análise rápida e orientação segura
            </h2>
            <p className="text-lg text-white/80 mb-8">
              Apresente a documentação do caso à RDC Advogados e receba orientação jurídica clara sobre os caminhos
              possíveis.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contato">
                <Button
                  size="lg"
                  data-testid="plano-saude-contact-button"
                  className="bg-white text-[#2A2A2A] hover:bg-white/90 rounded-sm font-medium px-8 py-6 text-base"
                >
                  Analisar negativa
                </Button>
              </Link>
              <a href="https://wa.me/552128880258" target="_blank" rel="noopener noreferrer">
                <Button
                  size="lg"
                  
                  data-testid="plano-saude-whatsapp-button"
                  className="bg-[#25D366] text-white hover:bg-[#20BA5A] border-0 rounded-sm font-medium px-8 py-6 text-base"
                >
                  Falar pelo WhatsApp
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PlanoSaude;